import { useState, useEffect } from 'react';
import { getCart } from '../firebase/cart-functions';
import { getStoreSettings } from '../firebase/settings-functions';
import { DEFAULT_STORE_ID } from '../firebase/firebase';
import { useLanguage } from '../context/LanguageContext';

export const useCheckoutData = (currentUser) => {
    const { language } = useLanguage();
    const [cart, setCart] = useState({ items: [], subtotal: 0 });
    const [storeSettings, setStoreSettings] = useState({
        currency: "AED",
        shippingOptions: [],
        paymentMethods: {},
        bankTransferDetails: { en: "", ar: "" },
        availableCountries: [],
        taxRate: 0,
        taxIncluded: false,
        requiresShipping: true // Default to true
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                // Fetch store settings with proper multi-tenant support
                let settings;
                try {
                    // console.log('Fetching multilingual store settings for store:', DEFAULT_STORE_ID, 'language:', language);
                    settings = await getStoreSettings(DEFAULT_STORE_ID, { language, simplified: true });

                    // Ensure we have all required fields from the store settings
                    const completeSettings = {
                        currency: settings.currency || "AED",
                        shippingOptions: settings.shippingOptions,
                        paymentMethods: settings.paymentMethods || { cashOnDelivery: true },
                        bankTransferDetails: settings.bankTransferDetails || { en: "", ar: "" },
                        availableCountries: settings.availableCountries,
                        // IMPORTANT: Use actual store tax settings
                        taxRate: settings.taxRate || 0,
                        taxIncluded: settings.taxIncluded || false,
                        // Include shipping requirement flag
                        requiresShipping: settings.requiresShipping !== false, // Default to true for backward compatibility
                        // Include any other store-specific settings
                        name: settings.name || "",
                        storeId: DEFAULT_STORE_ID
                    };

                    // console.log('Store settings loaded:', completeSettings);
                    settings = completeSettings;

                } catch (settingsError) {
                    console.error("Error fetching store settings:", settingsError);


                }
                setStoreSettings(settings);

                // Fetch cart data with FRESH pricing for checkout - ensure it's filtered by current store
                let cartData;
                try {
                    if (currentUser) {
                        // console.log('Fetching cart for authenticated user:', currentUser.uid);
                        const rawCartData = await getCart(currentUser, true); // ✅ Enable fresh data enrichment for checkout
                        cartData = filterCartByStore(rawCartData, DEFAULT_STORE_ID);
                    } else {
                        // console.log('Fetching cart for guest user from localStorage');
                        const rawCartData = await getCart(null, true); // ✅ Enable fresh data enrichment for guests too
                        cartData = filterCartByStore(rawCartData, DEFAULT_STORE_ID);
                    }
                    // console.log('Cart data loaded:', cartData);
                } catch (cartError) {
                    console.error("Error accessing cart:", cartError);
                    cartData = getLocalStorageCart();
                }

                setCart(cartData);
            } catch (err) {
                console.error("Error fetching checkout data:", err);
                setError("Failed to load checkout information. Please try again.");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [currentUser, language]);

    const filterCartByStore = (cartData, storeId) => {
        if (!cartData || !cartData.items || !Array.isArray(cartData.items)) {
            return { items: [], subtotal: 0, storeId };
        }

        // console.log('Filtering cart items for store:', storeId);
        // console.log('Original cart items:', cartData.items);

        // Filter items that belong to the current store
        const filteredItems = cartData.items.filter(item => {
            const itemStoreId = item.storeId || DEFAULT_STORE_ID;
            return itemStoreId === storeId;
        });

        // console.log('Filtered cart items:', filteredItems);

        // Recalculate subtotal for filtered items
        const filteredSubtotal = filteredItems.reduce((total, item) => {
            const itemPrice = parseFloat(item.price) || 0;
            const itemQuantity = parseInt(item.quantity) || 0;
            return total + (itemPrice * itemQuantity);
        }, 0);

        return {
            items: filteredItems,
            subtotal: filteredSubtotal,
            storeId: storeId
        };
    };

    const getLocalStorageCart = () => {
        try {
            const cartString = localStorage.getItem('guestCart');
            const cart = cartString ? JSON.parse(cartString) : null;

            if (cart && Array.isArray(cart.items)) {
                return filterCartByStore(cart, DEFAULT_STORE_ID);
            }
        } catch (error) {
            console.error("Error parsing localStorage cart:", error);
        }

        return { items: [], subtotal: 0, storeId: DEFAULT_STORE_ID };
    };

    return {
        cart,
        setCart,
        storeSettings,
        loading,
        error
    };
};